import ErrorPage from "@/components/pages/ErrorPage";
import {
    consentKeys,
    fetchConsentById,
    resolveConsentReadLocale,
    useSuspenseConsentById,
} from "@/features/consent/api/selectConsentById";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import {
    buildPatientDisplayNameInput,
    getLocalizedPatientDisplayName,
} from "@/features/patients/utils/localizedPatientName";
import { hooks as h } from "@/hooks";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

function PatientDiaryConsentLayout() {
    const copy = usePatientsCopy();
    const diaryCopy = useDiaryCopy();
    const { locale } = useAppLocale();
    const { consentId, diaryId } = Route.useParams();
    const { data: consent } = useSuspenseConsentById(consentId);
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const { data: profile } = useProfileByIdQuerySafe(diary?.patient_id);
    const patientDisplayNameInput = buildPatientDisplayNameInput(profile, diary?.patient_info);
    const patientName = getLocalizedPatientDisplayName(patientDisplayNameInput, locale) || copy.patientFallback;

    h.useNav([
        { label: copy.patients, route: { to: "/patients" } },
        {
            label: patientName,
            route: { to: "/patients/$diaryId", params: { diaryId } },
        },
        {
            label: consent?.consent_name || diaryCopy.consentForm,
            route: { to: "/patients/$diaryId/consent/$consentId", params: { diaryId, consentId } },
        },
    ]);

    return <Outlet />;
}

export const Route = createFileRoute("/_app/patients/$diaryId/consent/$consentId")({
    component: PatientDiaryConsentLayout,
    errorComponent: ({ error, reset }) => <ErrorPage error={error as Error} reset={reset} />,
    loader: async ({ context: { queryClient }, params }) => {
        const locale = resolveConsentReadLocale();
        const consent = await queryClient.ensureQueryData({
            queryKey: consentKeys.byId(params.consentId, locale),
            queryFn: () => fetchConsentById(params.consentId, locale),
        });

        if (!consent || consent.diary_id !== params.diaryId) {
            throw redirect({
                to: "/patients/$diaryId",
                params: { diaryId: params.diaryId },
            });
        }

        return { consentId: params.consentId };
    },
});
